/**
 * Aircraft Models CRUD Operations
 * Functions for managing aircraft models used in gallery tagging
 */

import {
  Firestore,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { AircraftModel, createAircraftModel, generateSlug } from '@/models/aircraft-model';
import { AircraftType } from '@/models/aircraft';

const COLLECTION_NAME = 'aircraftModels';

/**
 * Get all aircraft models, optionally filtered by type
 */
export async function getAircraftModels(
  firestore: Firestore,
  options?: {
    type?: AircraftType;
    sortBy?: 'name' | 'usageCount' | 'createdAt';
    limitCount?: number;
  }
): Promise<AircraftModel[]> {
  const modelsRef = collection(firestore, COLLECTION_NAME);

  let q = query(modelsRef);

  if (options?.type) {
    q = query(q, where('type', '==', options.type));
  }

  // Apply sorting
  const sortField = options?.sortBy || 'name';
  if (sortField === 'usageCount') {
    q = query(q, orderBy('usageCount', 'desc'));
  } else if (sortField === 'createdAt') {
    q = query(q, orderBy('createdAt', 'desc'));
  } else {
    q = query(q, orderBy('name', 'asc'));
  }

  if (options?.limitCount) {
    q = query(q, limit(options.limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as AircraftModel));
}

/**
 * Get a single aircraft model by ID
 */
export async function getAircraftModel(
  firestore: Firestore,
  modelId: string
): Promise<AircraftModel | null> {
  const docRef = doc(firestore, COLLECTION_NAME, modelId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as AircraftModel;
}

/**
 * Get aircraft model by slug
 */
export async function getAircraftModelBySlug(
  firestore: Firestore,
  slug: string
): Promise<AircraftModel | null> {
  const modelsRef = collection(firestore, COLLECTION_NAME);
  const q = query(modelsRef, where('slug', '==', slug), limit(1));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as AircraftModel;
}

/**
 * Search aircraft models by name
 */
export async function searchAircraftModels(
  firestore: Firestore,
  searchTerm: string,
  limitCount: number = 10
): Promise<AircraftModel[]> {
  // Firestore doesn't support full-text search, so we fetch all and filter client-side
  // For production, consider using Algolia or similar
  const models = await getAircraftModels(firestore, { sortBy: 'usageCount' });

  const searchLower = searchTerm.toLowerCase();
  return models
    .filter(model =>
      model.name.toLowerCase().includes(searchLower) ||
      model.manufacturer?.toLowerCase().includes(searchLower)
    )
    .slice(0, limitCount);
}

/**
 * Create a new aircraft model
 */
export async function createAircraftModelDoc(
  firestore: Firestore,
  data: {
    name: string;
    type: AircraftType;
    manufacturer?: string;
    imageUrl?: string;
    createdBy: string;
  }
): Promise<AircraftModel> {
  // Check for duplicate slug
  const slug = generateSlug(data.name);
  const existing = await getAircraftModelBySlug(firestore, slug);

  if (existing) {
    throw new Error(`An aircraft model with the name "${data.name}" already exists`);
  }

  const modelData = createAircraftModel({
    name: data.name,
    type: data.type,
    manufacturer: data.manufacturer,
    imageUrl: data.imageUrl,
    createdBy: data.createdBy,
    slug,
  });

  const modelsRef = collection(firestore, COLLECTION_NAME);
  const docRef = await addDoc(modelsRef, modelData);

  return {
    id: docRef.id,
    ...modelData,
  };
}

/**
 * Update an aircraft model
 */
export async function updateAircraftModel(
  firestore: Firestore,
  modelId: string,
  updates: Partial<Pick<AircraftModel, 'name' | 'type' | 'manufacturer' | 'imageUrl'>>
): Promise<void> {
  const docRef = doc(firestore, COLLECTION_NAME, modelId);

  const updateData: Record<string, unknown> = {
    ...updates,
    updatedAt: Timestamp.now(),
  };

  // Update slug if name changed
  if (updates.name) {
    updateData.slug = generateSlug(updates.name);
  }

  await updateDoc(docRef, updateData);
}

/**
 * Delete an aircraft model
 */
export async function deleteAircraftModel(
  firestore: Firestore,
  modelId: string
): Promise<void> {
  const docRef = doc(firestore, COLLECTION_NAME, modelId);
  await deleteDoc(docRef);
}

/**
 * Increment the usage count for an aircraft model
 */
export async function incrementModelUsageCount(
  firestore: Firestore,
  modelId: string,
  delta: number = 1
): Promise<void> {
  const docRef = doc(firestore, COLLECTION_NAME, modelId);
  await updateDoc(docRef, {
    usageCount: increment(delta),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Get multiple aircraft models by their IDs
 */
export async function getAircraftModelsByIds(
  firestore: Firestore,
  modelIds: string[]
): Promise<AircraftModel[]> {
  if (modelIds.length === 0) return [];

  const models: AircraftModel[] = [];

  // Fetch in parallel but respect Firestore's batch limits
  const batchSize = 10;
  for (let i = 0; i < modelIds.length; i += batchSize) {
    const batch = modelIds.slice(i, i + batchSize);
    const promises = batch.map(id => getAircraftModel(firestore, id));
    const results = await Promise.all(promises);
    models.push(...results.filter((m): m is AircraftModel => m !== null));
  }

  return models;
}
